/**
 * Deploying the engine replaces every hashed bundle in one atomic swap, so a
 * tab that was opened before a release still references chunks that no longer
 * exist. The next lazy import (a route or a translation file) then fails and
 * leaves the respondent on a dead page.
 *
 * Answers, surveys and the temporary response key are persisted to local
 * storage, so reloading recovers the session almost transparently. Reloads are
 * rate limited per tab so a genuinely broken build cannot cause a reload loop,
 * while a tab that is left open across several releases can still recover.
 */
const RELOAD_FLAG = 'feedback-chunk-reload';
const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;
const CHUNK_ERROR_PATTERNS = [
    /loading chunk \S+ failed/i,
    /loading css chunk \S+ failed/i,
    /failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /importing a module script failed/i
];
export function isChunkLoadError(error) {
    if (!error) {
        return false;
    }
    const candidate = error;
    if (candidate.name === 'ChunkLoadError') {
        return true;
    }
    const message = typeof candidate.message === 'string' ? candidate.message : String(error);
    return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(message));
}
function claimReloadAttempt() {
    try {
        const previous = Number(window.sessionStorage.getItem(RELOAD_FLAG));
        if (previous && Date.now() - previous < RELOAD_COOLDOWN_MS) {
            return false;
        }
        window.sessionStorage.setItem(RELOAD_FLAG, Date.now().toString());
        return true;
    }
    catch {
        // Session storage can be unavailable (private mode, blocked cookies). Without
        // a loop guard it is safer to leave the page alone than to risk reloading it
        // indefinitely.
        return false;
    }
}
export function recoverFromChunkLoadError(error) {
    if (!isChunkLoadError(error) || !claimReloadAttempt()) {
        return false;
    }
    window.location.reload();
    return true;
}
export function registerChunkReloadHandlers(router) {
    router.onError(error => {
        recoverFromChunkLoadError(error);
    });
    window.addEventListener('unhandledrejection', event => {
        recoverFromChunkLoadError(event.reason);
    });
    // Capture phase: failures to load a script or stylesheet fire on the element
    // itself and never bubble up to the window.
    window.addEventListener('error', event => {
        recoverFromChunkLoadError(event.error ?? event.message);
    }, true);
}
